// 扑克牌牌面（作者 2026-11-02：“扑克牌要有扑克牌的样子”）：左上、右下（倒置）两个角标；
// 2–10 按标准点阵排花色（下半部倒置），A 牌心一个大花色，J/Q/K 与大小王牌心是双头人物插画（作者 11-03），大小王角标竖排 JOKER。
// 作者 11-02：所有牌样式统一，去掉宫廷牌与大小王牌心的细线内框。
// 点数牌用文字与 CSS 画；人物插画是本项目原创生成的图（许可干净）。
import { isJoker, rankName } from './game/cards.ts';
import type { Card } from './game/cards.ts';

const SUIT: Record<string, string> = { S: '♠', H: '♥', C: '♣', D: '♦' };

/** 标准点阵：[x%, y%, 是否倒置]。 */
const L = 28, C = 50, R = 72;
export const PIPS: Record<number, [number, number, boolean][]> = {
  2: [[C, 20, false], [C, 80, true]],
  3: [[C, 20, false], [C, 50, false], [C, 80, true]],
  4: [[L, 20, false], [R, 20, false], [L, 80, true], [R, 80, true]],
  5: [[L, 20, false], [R, 20, false], [C, 50, false], [L, 80, true], [R, 80, true]],
  6: [[L, 20, false], [R, 20, false], [L, 50, false], [R, 50, false], [L, 80, true], [R, 80, true]],
  7: [[L, 20, false], [R, 20, false], [C, 35, false], [L, 50, false], [R, 50, false], [L, 80, true], [R, 80, true]],
  8: [[L, 20, false], [R, 20, false], [C, 35, false], [L, 50, false], [R, 50, false], [C, 65, true], [L, 80, true], [R, 80, true]],
  9: [[L, 20, false], [R, 20, false], [L, 40, false], [R, 40, false], [C, 50, false], [L, 60, true], [R, 60, true], [L, 80, true], [R, 80, true]],
  10: [[L, 20, false], [R, 20, false], [C, 30, false], [L, 40, false], [R, 40, false], [L, 60, true], [R, 60, true], [C, 70, true], [L, 80, true], [R, 80, true]],
};
const COURT: Record<number, string> = { 11: 'J', 12: 'Q', 13: 'K' };
/**
 * 人头牌与大小王的人物插画（作者 11-03 给的人物原型：K 大卫王 / 查理曼 / 亚历山大 / 凯撒，Q 雅典娜 / 朱迪斯 / 阿金妮 / 拉结，
 * J 奥吉尔 / 拉海尔 / 兰斯洛特 / 罗兰；大小王为宫廷小丑）。传统扑克半身双头版式；画风与 8 位角色头像同一套 Q 版、牌桌淡雅配色
 * （作者 11-03：要和游戏整体 UI 一致，v1 朱墨木刻已弃用），本地 gpt-image2.5 原创生成；
 * `tools/compose_court_art.py` 拼成 public/cards/<花色><点数>.webp（透明底）。原图与提示词：产出目录 02_界面设计/人头牌_v2/。
 */
export const ART_IDS = ['S', 'H', 'C', 'D'].flatMap((s) => [11, 12, 13].map((r) => `${s}${r}`)).concat(['J16', 'J17']);
// 性能（作者 11-03）：首页开着时牌桌在首页底下已经画好，人头牌图先只记地址（data-src），离开首页再换上（main.ts syncAtHome），不跟首页插画抢网速
const art = (id: string) => (typeof document !== 'undefined' && document.body?.classList.contains('at-home')
  ? `<img class="art-img" data-src="cards/${id}.webp" alt="" draggable="false" decoding="async">`
  : `<img class="art-img" src="cards/${id}.webp" alt="" draggable="false" decoding="async">`);

export function cardFace(c: Card): string {
  if (isJoker(c)) {
    return `<span class="idx tl joker-idx">JOKER</span><span class="idx br joker-idx">JOKER</span>`
      + `<span class="face court art joker-face" aria-hidden="true">${art(`J${c.rank}`)}</span>`; // 作者 11-03：大王彩色小丑、小王单色小丑；不写“大王 / 小王”字，读屏仍念牌名
  }
  const s = SUIT[c.suit], r = rankName(c.rank);
  const idx = `<span class="idx tl">${r}<small>${s}</small></span><span class="idx br">${r}<small>${s}</small></span>`;
  if (c.rank === 14) return `${idx}<span class="face ace"><i>${s}</i></span>`;
  if (COURT[c.rank]) return `${idx}<span class="face court art" aria-hidden="true">${art(`${c.suit}${c.rank}`)}</span>`;
  const pips = PIPS[c.rank].map(([x, y, flip]) => `<i style="left:${x}%;top:${y}%"${flip ? ' class="flip"' : ''}>${s}</i>`).join('');
  return `${idx}<span class="face pips">${pips}</span>`;
}
